import { useEffect, useState, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/store/useAppStore'
import { useNavigate } from 'react-router-dom'
import { Search, Hash, CheckSquare, ArrowRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/utils'

export function useSearchModal() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return { open, setOpen }
}

export function SearchModal({ open, onClose }) {
  const { tasks, projects, setSelectedTask, setActiveProject } = useAppStore()
  const [query, setQuery] = useState('')
  const inputRef = useRef()
  const navigate = useNavigate()
  const [activeIdx, setActiveIdx] = useState(0)

  useEffect(() => {
    if (open) { setQuery(''); setActiveIdx(0); setTimeout(() => inputRef.current?.focus(), 50) }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const { results, matchedProjects, matchedTasks } = useMemo(() => {
    const q = query.toLowerCase().trim()
    const mTasks = q
      ? tasks.filter((t) =>
          t.title.toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q)
        ).slice(0, 8)
      : tasks.filter((t) => !t.completed).slice(0, 5)
    const mProjects = q
      ? projects.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 4)
      : projects.slice(0, 3)
    return {
      matchedProjects: mProjects,
      matchedTasks: mTasks,
      results: [
        ...mProjects.map((p) => ({ type: 'project', item: p })),
        ...mTasks.map((t) => ({ type: 'task', item: t })),
      ],
    }
  }, [query, tasks, projects])

  useEffect(() => { setActiveIdx(0) }, [query])

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)) }
    if (e.key === 'Enter' && results[activeIdx]) selectResult(results[activeIdx])
  }

  function selectResult({ type, item }) {
    if (type === 'project') {
      setActiveProject(item.id)
      navigate(`/project/${item.id}`)
    } else {
      setActiveProject(item.projectId)
      navigate(`/project/${item.projectId}`)
      setTimeout(() => setSelectedTask(item.id), 100)
    }
    onClose()
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Search tasks and projects"
            initial={{ opacity: 0, scale: 0.97, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -8 }}
            transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
            className="fixed top-[15vh] left-1/2 -translate-x-1/2 z-50 w-full max-w-lg"
          >
            <div className="bg-surface border border-border rounded-2xl shadow-2xl shadow-black/60 overflow-hidden">
              {/* Input */}
              <div className="flex items-center gap-3 px-4 h-14 border-b border-border">
                <Search size={16} className="text-text-muted flex-shrink-0" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search tasks, projects…"
                  className="flex-1 bg-transparent text-sm text-text placeholder:text-text-muted focus:outline-none focus-visible:outline-none h-14 py-3"
                />
                {query && (
                  <button onClick={() => setQuery('')} aria-label="Clear search" className="text-text-faint hover:text-text-muted active:scale-[0.97]">
                    <X size={13} aria-hidden="true" />
                  </button>
                )}
                <kbd className="text-[10px] text-text-faint border border-border rounded px-1.5 py-0.5">esc</kbd>
              </div>

              {/* Results */}
              <div className="max-h-80 overflow-y-auto py-2">
                {/* Projects */}
                {matchedProjects.length > 0 && (
                  <div>
                    <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-text-faint">Projects</p>
                    {matchedProjects.map((p, i) => {
                      const idx = i
                      return (
                        <button
                          key={p.id}
                          onClick={() => selectResult({ type: 'project', item: p })}
                          onMouseEnter={() => setActiveIdx(idx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 h-10 transition-colors text-left',
                            activeIdx === idx ? 'bg-surface-raised' : 'hover:bg-surface-raised'
                          )}
                        >
                          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: p.color }} />
                          <span className="flex-1 text-sm text-text truncate">{p.name}</span>
                          <ArrowRight size={12} className={cn('transition-opacity', activeIdx === idx ? 'opacity-100 text-text-muted' : 'opacity-0')} />
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* Tasks */}
                {matchedTasks.length > 0 && (
                  <div>
                    <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-text-faint mt-1">Tasks</p>
                    {matchedTasks.map((t, i) => {
                      const idx = matchedProjects.length + i
                      const proj = projects.find((p) => p.id === t.projectId)
                      return (
                        <button
                          key={t.id}
                          onClick={() => selectResult({ type: 'task', item: t })}
                          onMouseEnter={() => setActiveIdx(idx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 h-10 transition-colors text-left',
                            activeIdx === idx ? 'bg-surface-raised' : 'hover:bg-surface-raised'
                          )}
                        >
                          <div className={cn(
                            'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                            t.completed ? 'bg-accent border-accent' : 'border-border'
                          )}>
                            {t.completed && <span className="text-black text-[9px]">✓</span>}
                          </div>
                          <span className={cn('flex-1 text-sm truncate', t.completed ? 'line-through text-text-muted' : 'text-text')}>{t.title}</span>
                          {proj && (
                            <span className="flex items-center gap-1 text-[10px] text-text-faint flex-shrink-0">
                              <span className="w-1.5 h-1.5 rounded-sm" style={{ background: proj.color }} />
                              {proj.name}
                            </span>
                          )}
                          <ArrowRight size={12} className={cn('transition-opacity ml-1', activeIdx === idx ? 'opacity-100 text-text-muted' : 'opacity-0')} />
                        </button>
                      )
                    })}
                  </div>
                )}

                {results.length === 0 && (
                  <div className="py-10 text-center text-sm text-text-faint">
                    No results for <span className="text-text-muted">"{query}"</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-2 border-t border-border flex items-center gap-3 text-[10px] text-text-faint">
                <span><kbd className="border border-border rounded px-1 py-0.5">↑↓</kbd> navigate</span>
                <span><kbd className="border border-border rounded px-1 py-0.5">↵</kbd> open</span>
                <span><kbd className="border border-border rounded px-1 py-0.5">esc</kbd> close</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
